import { Button, Text } from "@twinkltech/web-ui";
import { useId } from "react";
import { Link, useMatch, useResolvedPath } from "react-router-dom";

type TabLinkProps = {
  exactMatch?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
} & React.ComponentPropsWithoutRef<typeof Link>;

export const TabLink = ({
  children,
  className,
  exactMatch = true,
  to,
  ...props
}: TabLinkProps) => {
  const resolved = useResolvedPath(to);

  // Use useMatch to determine if the link is active
  // For exact matches, match the full path
  // For non-exact matches, use the wildcard pattern
  const match = useMatch({
    path: exactMatch ? resolved.pathname : `${resolved.pathname}/*`,
    end: exactMatch,
  });

  const isMatch = !!match || undefined;

  const ariaId = useId();

  return (
    <Button
      aria-labelledby={ariaId}
      as={Link}
      className={[
        className,
        // Base styles
        "border-2 font-normal",

        // Default/"Resting" state styles
        "border-neutral-150 bg-white",

        // pseudo-state styles
        "hover:border-blue-100 focus-within:border-blue-100",

        // Current filter styles
        "data-active:bg-blue-50 data-active:border-blue-200",

        // Disabled state styles
        "disabled:border-neutral-100! disabled:bg-neutral-100 disabled:text-neutral-500",
      ]
        .filter(Boolean)
        .join(" ")}
      data-active={isMatch}
      to={to}
      variant="outlined"
      {...props}
    >
      <Text id={ariaId} variant="base-normal">
        {children}
      </Text>
    </Button>
  );
};
